import { LineChart, ScaleTypes } from '@carbon/charts-react';
import * as React from 'react';
import { Body, Heading, Tab, Tabs } from 'vet-bones/design-system/components';

import { SduChartPoint } from 'src/ui/components/sdu-dashboard/aggregations';
import {
  SDU_DASHBOARD_CHART,
  SDU_DASHBOARD_CHART_GROUP_BY,
  SDU_DASHBOARD_CHART_PLACEHOLDER,
} from 'src/ui/constants/testIds';
import { TimeGrain } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

const TOGGLE_GRAINS = [TimeGrain.Day, TimeGrain.Week, TimeGrain.Month] as const;
type ToggleGrain = typeof TOGGLE_GRAINS[number];
const CHART_HEIGHT = '360px';
const CHART_LINE_CSS_VAR = '--sdu-chart-line';

const readChartLineColor = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return getComputedStyle(document.documentElement)
    .getPropertyValue(CHART_LINE_CSS_VAR)
    .trim();
};

const GRAIN_LABELS: Record<ToggleGrain, string> = {
  [TimeGrain.Day]: copy.components.sduDashboard.chart.timeGrain.day,
  [TimeGrain.Week]: copy.components.sduDashboard.chart.timeGrain.week,
  [TimeGrain.Month]: copy.components.sduDashboard.chart.timeGrain.month,
};

interface SduConsumptionChartProps {
  data: SduChartPoint[];
  isLoading?: boolean;
  onTimeGrainChange: (grain: TimeGrain) => void;
  timeGrain: TimeGrain;
  title: string;
}

export const SduConsumptionChart: React.VFC<SduConsumptionChartProps> = ({
  data,
  isLoading,
  onTimeGrainChange,
  timeGrain,
  title,
}) => {
  const placeholderText = isLoading
    ? copy.components.sduDashboard.loading
    : copy.components.sduDashboard.empty;

  const tabularData = React.useMemo(
    () =>
      // Anchor at local noon, not UTC midnight, so the Carbon TIME scale
      // (which formats ticks in local time) never shifts a bucket onto the
      // previous calendar day for users west of UTC. Without this, a
      // "2026-05-01" month bucket would render as "Apr 30" in US/Pacific.
      data.map((point) => ({
        date: new Date(`${point.date}T12:00:00`),
        group: copy.components.sduDashboard.chart.yAxis,
        value: point.totalUnitsConsumed,
      })),
    [data]
  );

  const lineColor = readChartLineColor();
  const chartOptions = {
    axes: {
      bottom: {
        mapsTo: 'date',
        scaleType: ScaleTypes.TIME,
        title: copy.components.sduDashboard.chart.xAxis,
      },
      left: {
        mapsTo: 'value',
        scaleType: ScaleTypes.LINEAR,
        title: copy.components.sduDashboard.chart.yAxis,
      },
    },
    color: lineColor
      ? { scale: { [copy.components.sduDashboard.chart.yAxis]: lineColor } }
      : undefined,
    height: CHART_HEIGHT,
    legend: { enabled: false },
    toolbar: { enabled: false },
  };

  return (
    <div className="organization-dashboard-card">
      <div className="sd-sdu-dashboard-chart-header">
        <Heading size="md" testId={SDU_DASHBOARD_CHART} weight="semibold">
          {title}
        </Heading>
        <div className="sd-sdu-dashboard-chart-header-actions">
          <Body size="sm">{copy.components.sduDashboard.chart.groupBy}</Body>
          <Tabs size="sm" variant="round">
            {TOGGLE_GRAINS.map((grain) => (
              <Tab
                key={grain}
                onClick={() => onTimeGrainChange(grain)}
                selected={grain === timeGrain}
                size="sm"
                testId={SDU_DASHBOARD_CHART_GROUP_BY(grain)}
              >
                {GRAIN_LABELS[grain]}
              </Tab>
            ))}
          </Tabs>
        </div>
      </div>
      {!data.length ? (
        <div className="sd-sdu-dashboard-chart-placeholder">
          <Body size="sm" testId={SDU_DASHBOARD_CHART_PLACEHOLDER}>
            {placeholderText}
          </Body>
        </div>
      ) : (
        <LineChart data={tabularData} options={chartOptions} />
      )}
    </div>
  );
};
